import { toast } from "@devhop/ui";
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  customerGroupsApi,
  customersApi,
  memberTiersApi,
} from "@/modules/customers/presentation/api/client";
import { geoApi } from "@/modules/geo/presentation/api/client";
import { inventoryApi } from "@/modules/inventory/presentation/api/client";
import { inventoryKeys } from "@/modules/inventory/presentation/api/queries";
import { orderKeys } from "@/modules/orders/presentation/api/queries";
import { paymentKeys } from "@/modules/payments/presentation/api/queries";
import type {
  PosCheckoutBody,
  PosQuickCreateCustomerBody,
  RecordSocialTransferPaymentBody,
} from "@/modules/pos/domain/contracts";
import type { FilterConditionDTO } from "@/shared/contracts/base";
import { posApi } from "./client";

export const posKeys = {
  all: ["pos"] as const,
  halProvinces: ["pos", "hal", "provinces"] as const,
  halDistricts: (provinceId: string | null) =>
    ["pos", "hal", "districts", provinceId] as const,
  halBranches: (
    provinceId: string | null,
    districtId: string | null,
    search: string,
  ) => ["pos", "hal", "branches", provinceId, districtId, search] as const,
};

const POS_STOCK_PAGE_SIZE = 40;

export function usePosStockInfiniteQuery(
  filters: FilterConditionDTO[] | undefined,
) {
  return useInfiniteQuery({
    queryKey: inventoryKeys.stock.productsCursorList({
      filters,
      limit: POS_STOCK_PAGE_SIZE,
    }),
    queryFn: ({ pageParam }) =>
      inventoryApi.stock.listProductsCursor({
        limit: POS_STOCK_PAGE_SIZE,
        after: pageParam,
        filters,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.pageInfo.nextCursor ?? undefined,
    enabled: true,
    refetchOnWindowFocus: false,
  });
}

export function usePosCheckoutMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: PosCheckoutBody) => posApi.checkout(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: orderKeys.all });
      qc.invalidateQueries({ queryKey: orderKeys.summary() });
      qc.invalidateQueries({ queryKey: inventoryKeys.stock.all });
      qc.invalidateQueries({ queryKey: ["reports"] });
    },
    onError: (error: unknown) => {
      toast.error("ຊຳລະ POS ລົ້ມເຫຼວ", {
        description: error instanceof Error ? error.message : undefined,
      });
    },
  });
}

export function usePosCustomerSearchQuery(q: string, enabled = true) {
  const trimmed = q.trim();
  const filters: FilterConditionDTO[] | undefined = trimmed
    ? [
        {
          field: "_or",
          op: "or" as const,
          value: [
            { field: "name", op: "contains" as const, value: trimmed },
            { field: "email", op: "contains" as const, value: trimmed },
            { field: "phoneNumber", op: "contains" as const, value: trimmed },
          ],
        },
      ]
    : undefined;

  return useQuery({
    queryKey: ["pos", "customers", "search", trimmed],
    queryFn: () => customersApi.list({ limit: 30, offset: 0, filters }),
    enabled: enabled && trimmed.length >= 1,
    staleTime: 30_000,
  });
}

export function usePosAllCustomersQuery(enabled = true) {
  return useQuery({
    queryKey: ["pos", "customers", "all"],
    queryFn: () => customersApi.list({ limit: 50, offset: 0 }),
    enabled,
    staleTime: 60_000,
  });
}

export function usePosQuickCreateCustomerMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: PosQuickCreateCustomerBody) =>
      posApi.quickCreateCustomer(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["pos", "customers"] });
    },
    onError: (error: unknown) => {
      toast.error("ສ້າງລູກຄ້າບໍ່ສຳເລັດ", {
        description: error instanceof Error ? error.message : undefined,
      });
    },
  });
}

export function usePosCustomerAddressesQuery(customerId: string | null) {
  return useQuery({
    queryKey: ["pos", "customers", customerId, "addresses"],
    queryFn: () => posApi.listCustomerAddresses(customerId ?? ""),
    enabled: !!customerId,
    staleTime: 60_000,
  });
}

export function usePosAllMemberTiersQuery() {
  return useQuery({
    queryKey: ["pos", "member-tiers"],
    queryFn: () => memberTiersApi.list({ limit: 100, offset: 0 }),
    staleTime: 5 * 60_000,
  });
}

export function usePosAllCustomerGroupsQuery() {
  return useQuery({
    queryKey: ["pos", "customer-groups"],
    queryFn: () => customerGroupsApi.list({ limit: 200, offset: 0 }),
    staleTime: 5 * 60_000,
  });
}

export function usePosProvincesQuery() {
  return useQuery({
    queryKey: ["pos", "geo", "provinces"],
    queryFn: () =>
      geoApi.provinces.list({
        limit: 300,
        offset: 0,
        sort: [{ field: "sortOrder", dir: "asc" }],
        filters: [{ field: "enabled", op: "eq", value: true }],
      }),
    staleTime: 10 * 60_000,
  });
}

export function usePosDistrictsQuery(provinceId: string | null) {
  return useQuery({
    queryKey: ["pos", "geo", "districts", provinceId],
    queryFn: () =>
      geoApi.districts.list({
        limit: 300,
        offset: 0,
        sort: [{ field: "sortOrder", dir: "asc" }],
        filters: [
          { field: "provinceId", op: "eq", value: provinceId ?? "" },
          { field: "enabled", op: "eq", value: true },
        ],
      }),
    enabled: !!provinceId,
    staleTime: 10 * 60_000,
  });
}

export function usePosHalProvincesQuery(enabled = true) {
  return useQuery({
    queryKey: posKeys.halProvinces,
    queryFn: () => posApi.listHalProvinces(),
    enabled,
    staleTime: 10 * 60_000,
  });
}

export function usePosHalDistrictsQuery(
  provinceId: string | null,
  enabled = true,
) {
  return useQuery({
    queryKey: posKeys.halDistricts(provinceId),
    queryFn: () => posApi.listHalDistricts(provinceId ?? ""),
    enabled: enabled && !!provinceId,
    staleTime: 10 * 60_000,
  });
}

export function usePosHalBranchesQuery(input: {
  provinceId?: string | null;
  districtId?: string | null;
  search?: string | null;
  enabled?: boolean;
}) {
  const provinceId = input.provinceId?.trim() || null;
  const districtId = input.districtId?.trim() || null;
  const search = input.search?.trim() ?? "";
  return useQuery({
    queryKey: posKeys.halBranches(provinceId, districtId, search),
    queryFn: () => posApi.listHalBranches({ provinceId, districtId, search }),
    enabled: input.enabled ?? true,
    staleTime: 10 * 60_000,
  });
}

export function useRecordSocialTransferPaymentMutation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: RecordSocialTransferPaymentBody) =>
      posApi.recordSocialTransferPayment(input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: orderKeys.all });
      qc.invalidateQueries({ queryKey: orderKeys.summary() });
      qc.invalidateQueries({ queryKey: orderKeys.detail(vars.orderId) });
      qc.invalidateQueries({ queryKey: paymentKeys.all });
      qc.invalidateQueries({ queryKey: inventoryKeys.stock.all });
      qc.invalidateQueries({ queryKey: ["reports"] });
    },
    onError: (error: unknown) => {
      toast.error("ບັນທຶກການໂອນບໍ່ສຳເລັດ", {
        description: error instanceof Error ? error.message : undefined,
      });
    },
  });
}
